export const ASSET_DRAG_MIME = "application/x-sc-asset";
export const ASSETS_CHANGED_EVENT = "sc-assets-changed";
export const PROJECTS_CHANGED_EVENT = "sc-projects-changed";
export const CREDITS_CHANGED_EVENT = "sc-credits-changed";

let lastAssetDragPayload = null;

export function assetDragPayload(asset) {
  if (!asset) return null;
  const rawId = asset.metadata?.raw_id ?? asset.project_id ?? asset.raw_id;
  if (rawId == null && !asset.id) return null;
  return {
    id: asset.id || "",
    asset_type: asset.asset_type || "",
    source_module: asset.source_module || "",
    raw_id: rawId,
    is_archived: !!asset.is_archived,
    project_id: asset.project_id,
    metadata: {
      raw_id: rawId,
      shoot_id: asset.metadata?.shoot_id,
    },
  };
}

export function setAssetDragData(event, asset) {
  const payload = assetDragPayload(asset);
  if (!payload) return false;
  lastAssetDragPayload = payload;
  if (event?.dataTransfer) {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(ASSET_DRAG_MIME, JSON.stringify(payload));
    event.dataTransfer.setData("text/plain", JSON.stringify(payload));
  }
  return true;
}

export function readAssetDragData(event) {
  try {
    const raw = event?.dataTransfer?.getData(ASSET_DRAG_MIME)
      || event?.dataTransfer?.getData("text/plain");
    if (raw) {
      if (raw.startsWith("{")) return JSON.parse(raw);
      if (lastAssetDragPayload && (raw === lastAssetDragPayload.id || raw === String(lastAssetDragPayload.raw_id || ""))) {
        return lastAssetDragPayload;
      }
      return { id: raw };
    }
  } catch { /* fall through to last payload */ }
  return lastAssetDragPayload;
}

export function notifyAssetsChanged() {
  try { window.dispatchEvent(new CustomEvent(ASSETS_CHANGED_EVENT)); } catch { /* ignore */ }
}

export function notifyProjectsChanged() {
  try { window.dispatchEvent(new CustomEvent(PROJECTS_CHANGED_EVENT)); } catch { /* ignore */ }
}

export function notifyCreditsChanged(balance) {
  try {
    const detail = typeof balance === "number" && Number.isFinite(balance) ? { balance } : {};
    window.dispatchEvent(new CustomEvent(CREDITS_CHANGED_EVENT, { detail }));
  } catch { /* ignore */ }
}

export function archiveEndpoint(asset) {
  const restoring = !!asset?.is_archived;
  const verb = restoring ? "restore" : "archive";
  switch (asset?.asset_type) {
    case "image": {
      const id = asset.metadata?.raw_id;
      if (id == null) return null;
      if (asset.source_module === "character_creator") {
        return { url: `/character-creator/characters/${id}/${verb}`, method: "patch" };
      }
      if (asset.source_module === "image_editor") {
        return { url: `/image-editor/${id}/${verb}`, method: "patch" };
      }
      if (asset.source_module === "upscaler") {
        return { url: `/upscaler/${id}/${verb}`, method: "patch" };
      }
      if (asset.source_module === "image_host") {
        return { url: `/photos/${id}/${verb}`, method: "patch" };
      }
      return { url: `/image-builder/images/${id}/${verb}`, method: "patch" };
    }
    case "listing": {
      const id = asset.metadata?.raw_id ?? asset.project_id ?? asset.id ?? asset.raw_id;
      if (id == null) return null;
      return { url: `/listing-builder/drafts/${id}/${verb}`, method: "patch" };
    }
    case "photo_shoot": {
      const shootId = asset.metadata?.shoot_id;
      const imgId = asset.metadata?.raw_id;
      if (!shootId || imgId == null) return null;
      return { url: `/photo-shoots/${shootId}/images/${imgId}/${verb}`, method: "patch" };
    }
    case "project": {
      const id = asset.metadata?.raw_id ?? asset.project_id;
      if (!id) return null;
      return { url: `/spark-creator/projects/${id}/${verb}`, method: "patch" };
    }
    case "video": {
      const id = asset.metadata?.raw_id ?? asset.project_id ?? asset.raw_id;
      if (id == null) return null;
      switch (asset.source_module) {
        case "cinema_studio":
          return { url: `/seedance-studio/videos/${id}/${verb}`, method: "patch" };
        case "video_builder":
          return restoring
            ? { url: `/video-builder/projects/${id}/restore`, method: "post" }
            : { url: `/video-builder/projects/${id}`, method: "delete" };
        case "ad_wizard": {
          const sceneId = asset.metadata?.scene_id;
          if (sceneId && !asset.metadata?.project_archived) {
            return { url: `/ad-wizard/scenes/${sceneId}/${verb}`, method: "patch" };
          }
          return { url: `/ad-wizard/projects/${id}/${verb}`, method: "patch" };
        }
        case "mimic_studio":
          return { url: `/mimic-studio/projects/${asset.project_id || id}/${verb}`, method: "patch" };
        case "upscaler":
          return { url: `/upscaler/${id}/${verb}`, method: "patch" };
        default: return null;
      }
    }
    default: return null;
  }
}

export function permanentDeleteEndpoint(asset) {
  switch (asset?.asset_type) {
    case "image": {
      const id = asset.metadata?.raw_id;
      if (id == null) return null;
      if (asset.source_module === "character_creator") {
        return { url: `/character-creator/characters/${id}` };
      }
      if (asset.source_module === "image_editor") {
        return { url: `/image-editor/${id}` };
      }
      if (asset.source_module === "upscaler") {
        return { url: `/upscaler/${id}` };
      }
      if (asset.source_module === "image_host") {
        return { url: `/photos/${id}` };
      }
      return { url: `/image-builder/images/${id}` };
    }
    case "listing": {
      const id = asset.metadata?.raw_id ?? asset.project_id ?? asset.id ?? asset.raw_id;
      if (id == null) return null;
      return { url: `/listing-builder/drafts/${id}` };
    }
    case "photo_shoot": {
      const shootId = asset.metadata?.shoot_id;
      const imgId = asset.metadata?.raw_id;
      if (!shootId || imgId == null) return null;
      return { url: `/photo-shoots/${shootId}/images/${imgId}` };
    }
    case "project": {
      const id = asset.metadata?.raw_id ?? asset.project_id;
      if (!id) return null;
      return { url: `/spark-creator/projects/${id}` };
    }
    case "video": {
      const id = asset.metadata?.raw_id ?? asset.project_id ?? asset.raw_id;
      if (id == null) return null;
      switch (asset.source_module) {
        case "cinema_studio":
          return { url: `/seedance-studio/videos/${id}` };
        case "video_builder":
        case "advanced":
          return { url: `/video-builder/projects/${id}/permanent` };
        case "ad_wizard": {
          const sceneId = asset.metadata?.scene_id;
          if (sceneId && !asset.metadata?.project_archived) {
            return { url: `/ad-wizard/scenes/${sceneId}` };
          }
          return { url: `/ad-wizard/projects/${id}` };
        }
        case "mimic_studio":
          return { url: `/mimic-studio/projects/${asset.project_id || id}` };
        case "upscaler":
          return { url: `/upscaler/${id}` };
        case "ugc_studio":
          return { url: `/ugc-studio/projects/${id}` };
        case "ugc_video":
          return { url: `/ugc-video/projects/${id}` };
        default:
          return null;
      }
    }
    default:
      return null;
  }
}
